import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Search, CheckCircle2, XCircle, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Alert } from '@/components/ui/Alert/Alert';
import { VStack, HStack } from '@/components/ui/Stack';
import { CventIntegrationService, type CventRfp } from '@/services/CventIntegrationService';
import { SettingsService } from '@/services/SettingsService';

interface CventRfpSearchComboboxProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (rfp: CventRfp) => void;
}

/**
 * Cvent RFP Search Combobox
 * 
 * Allows users to search and select a Cvent RFP to connect to an event
 */
export function CventRfpSearchCombobox({
  isOpen,
  onClose,
  onSelect,
}: CventRfpSearchComboboxProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [rfps, setRfps] = useState<CventRfp[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRfp, setSelectedRfp] = useState<CventRfp | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState(false);
  
  const cventService = useRef(new CventIntegrationService()).current;
  const settingsService = useRef(new SettingsService()).current;
  const isSearchingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Perform search with proper error handling and auth retry
  const performSearch = useCallback(async (searchString: string, retryAfterAuth = false) => {
    // Prevent multiple simultaneous searches
    if (isSearchingRef.current && !retryAfterAuth) {
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isSearchingRef.current = true;
    setLoading(true);
    setError(null);
    setAuthError(false);
    setHasSearched(true);

    try {
      const result = await cventService.searchRfps({
        searchString,
        limit: 20,
        statuses: ['ACTIVE', 'CANCELLED'],
      });
      
      // Check if request was aborted before updating state
      if (abortController.signal.aborted) {
        return;
      }
      
      setRfps(result.rfps); // REST API returns { rfps, totalCount, fetchedCount }
      setError(null);
    } catch (err: any) {
      // Don't process errors if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      console.error('Failed to search RFPs:', err);
      
      const errorMessage = err?.message || String(err);
      const statusCode = err?.statusCode || err?.response?.status;
      const errorCode = err?.code;
      
      // Check if it's an authentication error
      const isAuthError = 
        statusCode === 401 ||
        statusCode === 403 ||
        statusCode === 502 ||
        errorCode === 'EXTERNAL_SERVICE_ERROR' ||
        errorCode === 'AUTH_ERROR' ||
        errorMessage.includes('OAuth credentials not configured') || 
        errorMessage.includes('authentication required') ||
        errorMessage.includes('Authentication failed') ||
        errorMessage.includes('Cvent service unavailable');
      
      if (isAuthError) {
        // OAuth authentication error - user needs to configure credentials in Admin Settings
        setAuthError(true);
        setError('OAuth credentials not configured or invalid. Please configure them in Admin Settings → Cvent API tab.');
        setRfps([]);
      } else {
        // Non-auth error
        if (!abortController.signal.aborted) {
          setError(errorMessage);
          setRfps([]);
        }
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
        isSearchingRef.current = false;
        abortControllerRef.current = null;
      }
    }
  }, [cventService, settingsService]);

  // Handle search button click or Enter key
  const handleSearch = useCallback(() => {
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      setRfps([]);
      setHasSearched(false);
      setError(null);
      setAuthError(false);
      return;
    }

    performSearch(trimmed);
  }, [searchTerm, performSearch]);

  // Handle Enter key in input
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSearch();
    }
  }, [handleSearch, loading]);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Cancel any pending request when modal closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isSearchingRef.current = false;
    }
    
    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      isSearchingRef.current = false;
    };
  }, [isOpen]);

  function handleSelect() {
    if (selectedRfp) {
      onSelect(selectedRfp);
      handleClose();
    }
  }

  function handleClose() {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    isSearchingRef.current = false;
    setSearchTerm('');
    setRfps([]);
    setSelectedRfp(null);
    setHasSearched(false);
    setError(null);
    setAuthError(false);
    setLoading(false);
    onClose();
  }

  function getStatusIcon(status: string | undefined) {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle2 className="w-4 h-4 text-success-600" />;
      case 'CANCELLED':
        return <XCircle className="w-4 h-4 text-error-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  }

  function getStatusColor(status: string | undefined) {
    switch (status) {
      case 'ACTIVE':
        return 'text-success-700 bg-success-50';
      case 'CANCELLED':
        return 'text-error-700 bg-error-50';
      default:
        return 'text-gray-700 bg-gray-50';
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Connect to Cvent RFP"
      description="Search for a Cvent RFP to connect to this event. Type the RFP name, organization, or code."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSelect}
            disabled={!selectedRfp}
          >
            Connect
          </Button>
        </>
      }
    >
      <VStack gap={4}>
        {/* Search Input */}
        <HStack gap={2}>
          <Input
            type="text"
            placeholder="Search RFPs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            leftIcon={<Search className="w-4 h-4" />}
            autoFocus
            disabled={loading}
            className="flex-1"
          />
          <Button
            onClick={handleSearch}
            disabled={loading || !searchTerm.trim()}
            isLoading={loading}
            variant="primary"
            leftIcon={<Search className="w-4 h-4" />}
            size="md"
          >
            Search
          </Button>
        </HStack>

        {/* Error Alert */}
        {error && (
          <Alert variant={authError ? 'warning' : 'error'}>
            <VStack gap={2}>
              <p className="text-sm">{error}</p>
              {authError && (
                <HStack gap={2} align="center">
                  <Button
                    onClick={handleSearch}
                    variant="secondary"
                    size="sm"
                    leftIcon={<RefreshCw className="w-3 h-3" />}
                  >
                    Retry Search
                  </Button>
                  <a
                    href="/admin/settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-warning-700 hover:text-warning-800 underline inline-flex items-center gap-1"
                  >
                    Go to Admin Settings
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </HStack>
              )}
            </VStack>
          </Alert>
        )}

        {/* Results */}
        <div className="min-h-[300px] max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg">
          {loading && (
            <div className="py-12">
              <LoadingState text="Searching RFPs..." />
            </div>
          )}

          {!loading && !hasSearched && !error && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Start typing to search for RFPs
            </div>
          )}

          {!loading && hasSearched && rfps.length === 0 && !error && (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground mb-2">No RFPs found</p>
              <p className="text-xs text-muted-foreground">
                Try a different search term
              </p>
            </div>
          )}

          {!loading && rfps.length > 0 && (
            <div className="divide-y divide-gray-200">
              {rfps.map((rfp) => (
                <button
                  key={rfp.id}
                  onClick={() => setSelectedRfp(rfp)}
                  className={`
                    w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors
                    ${selectedRfp?.id === rfp.id ? 'bg-primary-50 hover:bg-primary-100' : ''}
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Radio indicator */}
                    <div className="mt-0.5">
                      <div
                        className={`
                          w-4 h-4 rounded-full border-2 flex items-center justify-center
                          ${
                            selectedRfp?.id === rfp.id
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300'
                          }
                        `}
                      >
                        {selectedRfp?.id === rfp.id && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                    </div>

                    {/* RFP Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusIcon(rfp.status)}
                        <h3 className="font-medium text-gray-900 truncate">
                          {rfp.name}
                        </h3>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-mono">{rfp.code}</span>
                        <span>•</span>
                        <span className={`px-2 py-0.5 rounded ${getStatusColor(rfp.status)}`}>
                          {rfp.status}
                        </span>
                        {rfp.totalBids !== undefined && (
                          <>
                            <span>•</span>
                            <span>{rfp.totalBids} {rfp.totalBids === 1 ? 'bid' : 'bids'}</span>
                          </>
                        )}
                      </div>

                      {rfp.organizationName && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {rfp.organizationName}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </VStack>
    </Modal>
  );
}

